export type Persona = {
  minDesk: number;
  maxDesk: number;
  minPrice: number;
  maxPrice: number;
  pricePerDesk: number;
  spaceType: string[];
  lat: number;
  lng: number;
  address: string;
  provider: string[];
  deskRangeCounts: Array<{ range: string; minDesk: number; maxDesk: number; count: number }>;
  source: "auto" | "form" | "filters";
  createdAt: string;
  expiresAt: string;
  isLocked: boolean;
};

const STORAGE_KEY = "office_hub_user_journey";

export function getCurrentRegionFromURL(): string {
  if (typeof window === "undefined") return "us";
  try {
    const pathname = window.location.pathname;
    const match = pathname.match(/^\/([a-z]{2,3})\//i);
    if (match && match[1]) return match[1].toLowerCase();
    return "us";
  } catch {
    return "us";
  }
}

function getDefaultPersona(): Persona {
  const now = new Date();
  const expiresAt = new Date(now.getTime() + 60 * 60 * 1000);
  return {
    minDesk: 0,
    maxDesk: 0,
    minPrice: 0,
    maxPrice: 0,
    pricePerDesk: 0,
    spaceType: [],
    lat: 0,
    lng: 0,
    address: "",
    provider: [],
    deskRangeCounts: [],
    source: "auto",
    createdAt: now.toISOString(),
    expiresAt: expiresAt.toISOString(),
    isLocked: false,
  };
}

function isPersonaEmpty(persona: Persona): boolean {
  const hasDesks = (persona.minDesk || 0) > 0 || (persona.maxDesk || 0) > 0;
  const hasPrice = (persona.minPrice || 0) > 0 || (persona.maxPrice || 0) > 0;
  const hasPricePerDesk = (persona.pricePerDesk || 0) > 0;
  const hasSpaceType = Array.isArray(persona.spaceType) && persona.spaceType.length > 0;
  const hasLocation = (persona.lat || 0) !== 0 || (persona.lng || 0) !== 0 || (persona.address || "").trim() !== "";
  const hasProvider = Array.isArray(persona.provider) && persona.provider.length > 0;
  const hasDeskRanges = Array.isArray(persona.deskRangeCounts) && persona.deskRangeCounts.length > 0;

  return !(hasDesks || hasPrice || hasPricePerDesk || hasSpaceType || hasLocation || hasProvider || hasDeskRanges);
}

function readPersonaForRegion(region: string): Persona {
  if (typeof window === "undefined") return getDefaultPersona();
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return getDefaultPersona();
    const parsed = JSON.parse(stored) as any;
    const personas = parsed?.personas;
    const raw = personas?.[region.toLowerCase()];
    if (!raw || typeof raw !== "object") return getDefaultPersona();
    return {
      ...getDefaultPersona(),
      ...raw,
      spaceType: Array.isArray(raw.spaceType) ? raw.spaceType : [],
      provider: Array.isArray(raw.provider) ? raw.provider : [],
      deskRangeCounts: Array.isArray(raw.deskRangeCounts) ? raw.deskRangeCounts : [],
    };
  } catch {
    return getDefaultPersona();
  }
}

export function getPersonaForApi(region?: string): Persona | null {
  const r = (region || getCurrentRegionFromURL()).toLowerCase();
  const persona = readPersonaForRegion(r);
  return isPersonaEmpty(persona) ? null : persona;
}

